import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { ResolutionProvider, useResolution } from '../../contexts/ResolutionContext';

import NotificationToast from '../Common/NotificationToast';
import LoadingOverlay from '../Common/LoadingOverlay';
import Timer from '../Common/Timer';
import { useLanguage } from '../../contexts/LanguageContext';

function SettingsContent() {
	const navigate = useNavigate();
	const { personId } = useParams();
	const { currentUser } = useAuth();
	const { person, activeYear, receiveWeeklyStatus, email, updateUserSettings, showSuccess, showError } =
		useResolution();

	// Form state
	const [displayName, setDisplayName] = useState('');
	const [emailAddress, setEmailAddress] = useState('');
	const [weeklyEmailEnabled, setWeeklyEmailEnabled] = useState(false);
	const [selectedTimerOption, setSelectedTimerOption] = useState(1);
	const [isLoading, setIsLoading] = useState(false);

	const { t } = useLanguage();

	// Initialize form with current user data
	useEffect(() => {
		if (person) {
			setDisplayName(person.displayName || `${person.name} ${person.surname}`.trim());
			setEmailAddress(email || '');
			setWeeklyEmailEnabled(receiveWeeklyStatus);
			setSelectedTimerOption(person.timerPreference || 'basic');
		}
	}, [person, email, receiveWeeklyStatus]);

	const handleSaveSettings = async () => {
		setIsLoading(true);
		try {
			// Update all settings including email
			const success = await updateUserSettings(
				{
					displayName: displayName.trim(),
					email: emailAddress.trim(),
					receiveWeeklyStatus: weeklyEmailEnabled,
					timerPreference: selectedTimerOption,
				},
				t
			);

			if (success) {
				showSuccess(t('settingsSavedSuccessfully'));
			} else {
				showError(t('failedToSaveSettings'));
			}
		} catch (error) {
			console.error('Error saving settings:', error);
			showError(t('errorSavingSettings'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleBackToProfile = () => {
		navigate(`/${personId}`);
	};

	const timerOptions = [
		{ id: 'basic', name: t('timer.option.basic.name'), description: t('timer.option.basic.desc') },
		{ id: 'card', name: t('timer.option.card.name'), description: t('timer.option.card.desc') },
		{ id: 'clock', name: t('timer.option.clock.name'), description: t('timer.option.clock.desc') },
		{ id: 'circular', name: t('timer.option.circular.name'), description: t('timer.option.circular.desc') },
		{ id: 'flip', name: t('timer.option.flip.name'), description: t('timer.option.flip.desc') },
		{ id: 'icon', name: t('timer.option.icon.name'), description: t('timer.option.icon.desc') },
	];

	if (!person) {
		return <LoadingOverlay />;
	}

	return (
		<div className='container mx-auto mt-8 p-4 max-w-4xl'>
			{/* Notification Toast */}
			<NotificationToast />

			{/* Loading Overlay */}
			<LoadingOverlay />

			{/* Header */}
			<div className='mb-8'>
				<div className='flex items-center justify-between'>
					<h1 className='text-3xl font-bold text-gray-800'>{t('settings')}</h1>
					<button
						onClick={handleBackToProfile}
						className='px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors'
					>
						{t('backToProfile')}
					</button>
				</div>
				<p className='text-gray-600 mt-2'>{t('customizeProfile')}</p>
			</div>

			{/* Settings Form */}
			<div className='space-y-8'>
				{/* Display Name Section */}
				<div className='bg-white p-6 rounded-lg shadow-sm border'>
					<h2 className='text-xl font-semibold text-gray-800 mb-4'>{t('displayNameHeading')}</h2>
					<div className='space-y-3'>
						<label className='block text-sm font-medium text-gray-700'>{t('displayNameHelp')}</label>
						<input
							type='text'
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
							placeholder={t('enterDisplayName')}
							maxLength={100}
						/>
						<p className='text-sm text-gray-500'>
							{t('currentlyShowing')}: "{person.name} {person.surname}"
						</p>
					</div>
				</div>

				{/* Email Preferences Section */}
				<div className='bg-white p-6 rounded-lg shadow-sm border'>
					<h2 className='text-xl font-semibold text-gray-800 mb-4'>{t('emailPreferences')}</h2>
					<div className='space-y-4'>
						<div>
							<label className='block text-sm font-medium text-gray-700 mb-2'>{t('emailAddress')}</label>
							<input
								type='email'
								value={emailAddress}
								onChange={(e) => setEmailAddress(e.target.value)}
								className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
								placeholder={t('emailAddress')}
							/>
							<p className='text-sm text-gray-500 mt-1'>{t('requiredForReceiving')}</p>
						</div>

						<label className='flex items-center space-x-3 cursor-pointer'>
							<input
								type='checkbox'
								checked={weeklyEmailEnabled}
								onChange={(e) => setWeeklyEmailEnabled(e.target.checked)}
								className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
							/>
							<div>
								<span className='text-sm font-medium text-gray-700'>{t('weeklyStatusEmail')}</span>
								<p className='text-sm text-gray-500'>
									{t('requiredForReceiving')}
									{!emailAddress.trim() && (
										<span className='text-orange-600'> {t('emailRequired')}</span>
									)}
								</p>
							</div>
						</label>
					</div>
				</div>

				{/* Timer Style Section */}
				<div className='bg-white p-6 rounded-lg shadow-sm border'>
					<h2 className='text-xl font-semibold text-gray-800 mb-4'>{t('timerStyle')}</h2>
					<p className='text-gray-600 mb-6'>{t('chooseTimer')}</p>

					{/* Timer Options */}
					<div className='space-y-6'>
						{timerOptions.map((option) => (
							<div key={option.id} className='space-y-3'>
								<label className='flex items-start space-x-3 cursor-pointer'>
									<input
										type='radio'
										name='timerOption'
										value={option.id}
										checked={selectedTimerOption === option.id}
										onChange={(e) => setSelectedTimerOption(e.target.value)}
										className='mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
									/>
									<div className='flex-1'>
										<div className='flex items-center space-x-2'>
											<span className='text-sm font-medium text-gray-700'>{option.name}</span>
											{selectedTimerOption === option.id && (
												<span className='px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full'>
													{t('selected')}
												</span>
											)}
										</div>
										<p className='text-sm text-gray-500 mt-1'>{option.description}</p>
									</div>
								</label>

								{/* Timer Preview */}
								<div className='ml-7'>
									<div className='text-xs text-gray-500 mb-2'>{t('preview')}</div>
									<Timer
										targetYear={new Date().getFullYear() + 1}
										timerType={option.id}
										showEndedMessage={false}
										className='scale-90 transform origin-left'
									/>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Save Button */}
				<div className='flex justify-end space-x-4'>
					<button
						onClick={handleBackToProfile}
						className='px-6 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors'
					>
						{t('cancel')}
					</button>
					<button
						onClick={handleSaveSettings}
						disabled={isLoading || !displayName.trim() || (weeklyEmailEnabled && !emailAddress.trim())}
						className='px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
					>
						{isLoading ? t('saving') : t('saveSettings')}
					</button>
				</div>
			</div>
		</div>
	);
}

// Main exported component with context provider
export default function Settings() {
	const { personId } = useParams();

	return (
		<ResolutionProvider personId={personId}>
			<SettingsContent />
		</ResolutionProvider>
	);
}
